import { CellValue, FixtureInfo, FixtureStats, FixturePlayerStats, RowValues, PlayerStats } from "@/types";
import { statParamToName } from "./names";
import { Tooltip } from "@heroui/react";
import "../styles/gameTable.css";

function getPlayerImagePath(playerId: number) {
    return `https://api.craquestats.com.br/logos/players/${playerId}.png`;
}

function makeUniqueArray(arr: any[]) {
    return Array.from(new Set(arr));
}

export const FINISHED_STATES = [5, 7, 8];
export const FINISHED_STATES_STRING = FINISHED_STATES.join(',');

export const PLAYER_STAT_KEYS = makeUniqueArray([
    'MINUTES_PLAYED',
    'GOALS_CONCEDED',
    'PASSES',
    'ACCURATE_PASSES',
    'RATING',
    'TOTAL_DUELS',
    'ACCURATE_PASSES_PERCENTAGE',
    'DUELS_WON',
    'DUELS_LOST',
    'TACKLES',
    'CLEARANCES',
    'AERIALS_WON',
    'FOULS',
    'SHOTS_TOTAL',
    'LONG_BALLS',
    'FOULS_DRAWN',
    'TOTAL_CROSSES',
    'DRIBBLED_ATTEMPTS',
    'INTERCEPTIONS',
    'KEY_PASSES',
    'DISPOSSESSED',
    'LONG_BALLS_WON',
    'DRIBBLED_PAST',
    'SUCCESSFUL_DRIBBLES',
    'YELLOWCARDS',
    'SHOTS_ON_TARGET',
    'SHOTS_BLOCKED',
    'ACCURATE_CROSSES',
    'GOALS',
    'SHOTS_OFF_TARGET',
    'BLOCKED_SHOTS',
    'BIG_CHANCES_CREATED',
    'TOUCHES',
    'GOALKEEPER_GOALS_CONCEDED',
    'OFFSIDES',
    'ASSISTS',
    'CAPTAIN',
    'SAVES',
    'BIG_CHANCES_MISSED',
    'SAVES_INSIDE_BOX',
    'HIT_WOODWORK',
    'PENALTIES_SCORED',
    'AERIALS_LOST',
    'PENALTIES_COMMITTED',
    'REDCARDS',
    'ERROR_LEAD_TO_GOAL',
    'PENALTIES_WON',
    'OWN_GOALS',
    'CLEARANCE_OFFLINE',
    'YELLOWRED_CARDS',
    'PENALTIES_MISSES',
    'PUNCHES',
    'PENALTIES_SAVED',
    'GOAL_KICKS',

    'CLEARANCES',
    'DISPOSSESSED',
    'ERROR_LEAD_TO_GOAL',
    'LONG_BALLS',
    'LONG_BALLS_WON',
    'THROUGH_BALLS',
    'THROUGH_BALLS_WON',
])

export const STAT_KEYS_LEAGUES = makeUniqueArray([
    'ASSISTS',
    'CORNERS',
    'GOALS',
    'OFFSIDES',
    'OWN_GOALS',
    'PASSES',
    'ACCURATE_PASSES',
    'REDCARDS',
    'SHOTS_BLOCKED',
    'SHOTS_TOTAL',
    'TOTAL_CROSSES',
    'YELLOWCARDS',
    'YELLOWRED_CARDS',
    'GOAL_KICKS',
    'SHOTS_ON_TARGET',
    'TACKLES',
    'FOULS',
    'FOULS_DRAWN',
])

export const STAT_KEYS_TEAMS = makeUniqueArray([
    'ACCURATE_CROSSES',
    'ACCURATE_PASSES',
    'ASSISTS',
    'BIG_CHANCES_CREATED',
    'BIG_CHANCES_MISSED',
    'BLOCKED_SHOTS',
    'CORNERS',
    'FOULS_DRAWN',
    'FOULS',
    'TACKLES',
    'GOALS',
    'HIT_WOODWORK',
    'LONG_BALLS',
    'OFFSIDES',
    'OWN_GOALS',
    'PASSES',
    'REDCARDS',
    'SHOTS_BLOCKED',
    'SHOTS_OFF_TARGET',
    'SHOTS_ON_TARGET',
    'SHOTS_TOTAL',
    'TOTAL_CROSSES',
    'YELLOWCARDS',
    'YELLOWRED_CARDS',
    'GOAL_KICKS',
    'BALL_POSSESSION',
    'CLEANSHEET',
    'DANGEROUS_ATTACKS'
])

export const STAT_KEYS_FIXTURES = makeUniqueArray([
    'ACCURATE_CROSSES',
    'ACCURATE_PASSES',
    'AERIALS_LOST',
    'AERIALS_WON',
    'ASSISTS',
    'BALL_POSSESSION', // Added
    'BIG_CHANCES_CREATED',
    'BIG_CHANCES_MISSED',
    'BLOCKED_SHOTS',
    'CLEARANCES',
    'DRIBBLED_ATTEMPTS',
    'DUELS_LOST',
    'DUELS_WON',
    'DUELS_TOTAL',
    'ERROR_LEAD_TO_GOAL',
    'FOULS',
    'FOULS_DRAWN',
    'GOALS',
    'HIT_WOODWORK',
    'INTERCEPTIONS',
    'KEY_PASSES',
    'LONG_BALLS',
    'LONG_BALLS_WON',
    'OFFSIDES',
    'OWN_GOALS',
    'PASSES',
    'PENALTIES_COMMITTED',
    'PENALTIES_SAVED',
    'PENALTIES_SCORED',
    'REDCARDS',
    'SAVES',
    'SAVES_INSIDE_BOX',
    'SHOTS_BLOCKED',
    'SHOTS_OFF_TARGET',
    'SHOTS_ON_TARGET',
    'SHOTS_TOTAL',
    'SUCCESSFUL_DRIBBLES',
    'TACKLES',
    'TOTAL_CROSSES',
    'TOUCHES',
    'YELLOWCARDS',
    'YELLOWRED_CARDS',
    'GOAL_KICKS'
])

export const STAT_KEYS_REFEREES = makeUniqueArray([
    'FOULS',
    'YELLOWCARDS',
    'REDCARDS',
])


function capAt90(value: number) {
    return value > 90 ? 90 : value;
}

function calculateFillPctg(minsPlayed: number | null | undefined, fixtureStatsItem: FixturePlayerStats) {
    if (fixtureStatsItem.enter_field_time) return [capAt90(fixtureStatsItem.enter_field_time) / 90 * 100, 100];
    if (fixtureStatsItem.exit_field_time) return [0, capAt90(fixtureStatsItem.exit_field_time) / 90 * 100];
    if (minsPlayed) return minsPlayed / 90 * 100;
    return;
}

export function getRowValuesForFixturePlayerStats(fixtureStats: FixturePlayerStats[], fixtureInfos: FixtureInfo[], playerId: string, premium: boolean) {
    var rowValues: RowValues[] = [];

    var allKeys: string[] = [];
    var valuesPerKey: { [key: string]: any } = {};
    var minsPlayedPerFixtureId: { [key: number]: number } = {};

    fixtureInfos.forEach((fixtureInfo) => {
        const fixtureStatsItem = fixtureStats.find((fixtureStat) => fixtureStat.fixture_id === fixtureInfo.id);
        if (fixtureStatsItem) {
            if (fixtureStatsItem['player_id'].toString() != playerId && premium) return;
            Object.keys((fixtureStatsItem['data'])).forEach((key) => {
                // iterate over all the stats keys available for this fixture
                if (!allKeys.includes(key)) {
                    allKeys.push(key);
                    valuesPerKey[key] = {};
                }

                if (key == 'MINUTES_PLAYED' && fixtureStatsItem['data'][key]) {
                    minsPlayedPerFixtureId[fixtureInfo.id] = fixtureStatsItem['data'][key];
                }
            });
        }
    });



    fixtureInfos.forEach((fixtureInfo) => {
        const fixtureStatsItem = fixtureStats.find((fixtureStat) => fixtureStat.fixture_id === fixtureInfo.id);
        if (fixtureStatsItem) {
            if (fixtureStatsItem['player_id'].toString() != playerId && premium) return;
            allKeys.forEach((key) => {
                let val = fixtureStatsItem['data'][key];
                valuesPerKey[key][fixtureInfo.id] = {
                    value: ((val == 0 || val) ? val : null),
                    fillPctg: calculateFillPctg(
                        minsPlayedPerFixtureId[fixtureInfo.id],
                        fixtureStatsItem
                    )
                };
            });
        } else {
            allKeys.forEach((key) => {
                valuesPerKey[key][fixtureInfo.id] = { value: null };
            });
        }
    });

    allKeys.forEach((key) => {
        if (!PLAYER_STAT_KEYS.includes(key)) return;
        rowValues.push({
            id: key,
            name: statParamToName(key),
            valuesByFixtureId: valuesPerKey[key],
            favoriteId: key
        });
    });
    return rowValues;
}

export function getRowValuesForFixtureStats(fixtureStats: FixtureStats[], fixtureInfos: FixtureInfo[], targetTeamId: 'sum' | number, statKeys: null | string[] = null) {
    var rowValues: RowValues[] = [];

    var allKeys: string[] = [];
    var valuesPerKey: { [key: string]: any } = {};

    fixtureInfos.forEach((fixtureInfo) => {
        const fixtureStatsItem = fixtureStats.find((fixtureStat) => fixtureStat.fixture_id === fixtureInfo.id);
        if (fixtureStatsItem) {
            let location: 'home' | 'away' = fixtureInfo.home_id == targetTeamId ? 'home' : 'away';
            Object.keys((fixtureStatsItem['data'][location])).forEach((key) => {
                // iterate over all the stats keys available for this fixture
                if (!allKeys.includes(key)) {
                    allKeys.push(key);
                    valuesPerKey[key] = {};
                }
            });
        }
    });


    fixtureInfos.forEach((fixtureInfo) => {
        const fixtureStatsItem = fixtureStats.find((fixtureStat) => fixtureStat.fixture_id === fixtureInfo.id);
        if (fixtureStatsItem) {
            let location: 'home' | 'away' = fixtureInfo.home_id == targetTeamId ? 'home' : 'away';
            allKeys.forEach((key) => {
                let val = fixtureStatsItem['data'][location][key];
                let otherLocation: 'home' | 'away' = location == 'home' ? 'away' : 'home';
                let sum = fixtureStatsItem['data']['home'][key] + fixtureStatsItem['data']['away'][key];
                if (targetTeamId == 'sum') val = sum;
                valuesPerKey[key][fixtureInfo.id] = {
                    value: ((val == 0 || val) ? val : null),
                    lowRightText: targetTeamId != 'sum' &&
                        (fixtureStatsItem['data'][otherLocation][key] || fixtureStatsItem['data'][otherLocation][key] === 0)
                        ?
                        <Tooltip content={'Contra'} delay={800} offset={-2} >
                            {fixtureStatsItem['data'][otherLocation][key].toString()}
                        </Tooltip>
                        : null,
                    lowRightValue: (fixtureStatsItem['data'][otherLocation][key] || fixtureStatsItem['data'][otherLocation][key] === 0)
                        ?
                        fixtureStatsItem['data'][otherLocation][key]
                        : null,
                    upLeftText: targetTeamId != 'sum' &&
                        (fixtureStatsItem['data'][otherLocation][key] || fixtureStatsItem['data'][otherLocation][key] === 0)
                        ?
                        <Tooltip content={'Soma = Casa + Visitante'} delay={800} offset={-2} >
                            {(fixtureStatsItem['data'][otherLocation][key] + val ).toString()}
                        </Tooltip>
                        : null,
                    upLeftValue: (fixtureStatsItem['data'][otherLocation][key] || fixtureStatsItem['data'][otherLocation][key] === 0)
                        ?
                        fixtureStatsItem['data'][otherLocation][key] + val
                        : null
                };
            });
        } else {
            allKeys.forEach((key) => {
                valuesPerKey[key][fixtureInfo.id] = { value: null };
            });
        }
    });

    allKeys.forEach((key) => {
        if (!(statKeys || (targetTeamId == 'sum' ? STAT_KEYS_LEAGUES : STAT_KEYS_TEAMS)).includes(key)) return;
        rowValues.push({
            id: key,
            name: statParamToName(key),
            valuesByFixtureId: valuesPerKey[key],
            favoriteId: key
        });
    });
    return rowValues;
}

export function getRowValuesForPlayerStats(
    playerStats: PlayerStats[],
    fixtureInfos: FixtureInfo[],
    teamId: number | null,
    statName: string,
    activePlayers?: boolean,
    typeFilter?: 'team' | 'league',
    sumSubstitutes?: boolean
): RowValues[] {
    const playerStatsMap: {
        [playerId: number]: {
            name: string,
            team_id: number,
            stats: { [fixtureId: number]: CellValue }
            imagePath?: string;
        }
    } = {};
    var minsPlayedPerPlayer: { [playerId: number]: { [fixtureId: number]: number } } = {};

    var fillPctgPerPlayer: {
        [playerId: number]: {
            [fixtureId: number]: number | undefined | number[]
        }
    } = {};

    playerStats.forEach(stat => {
        if (teamId != null && stat.team_id !== teamId) return;

        let value = stat.data[statName];

        if (sumSubstitutes) {
            const replacedById = stat.replaced_by_player_id;
            const replacedId = stat.replaced_player_id;

            const subs = [replacedById, replacedId].filter(id => id);

            if (subs.length) {
                // If there are substitutes, we need to sum their stats
                subs.forEach(subId => {
                    const subStat = playerStats.find(s => s.player_id === subId && s.fixture_id === stat.fixture_id);
                    if (subStat) {
                        value += subStat.data[statName] || 0;
                    }
                });
            }
        }

        if (stat.data['MINUTES_PLAYED']) {
            if (!minsPlayedPerPlayer[stat.player_id]) {
                minsPlayedPerPlayer[stat.player_id] = {};
            }
            minsPlayedPerPlayer[stat.player_id][stat.fixture_id] = stat.data['MINUTES_PLAYED'];
        }

        if (!fillPctgPerPlayer[stat.player_id]) {
            fillPctgPerPlayer[stat.player_id] = {};
        }

        fillPctgPerPlayer[stat.player_id][stat.fixture_id] = calculateFillPctg(
            stat.data['MINUTES_PLAYED'],
            stat
        );

        if (!playerStatsMap[stat.player_id]) {
            playerStatsMap[stat.player_id] = {
                name: stat.player_name,
                team_id: stat.team_id,
                imagePath: getPlayerImagePath(stat.player_id),
                stats: {}
            };
        }

        playerStatsMap[stat.player_id].stats[stat.fixture_id] = {
            value: value
        };
    });

    Object.entries(playerStatsMap).forEach(([playerIdStr, player]) => {
        let playerId = parseInt(playerIdStr);
        fixtureInfos.forEach(fixture => {
            if (!player.stats[fixture.id]) {
                player.stats[fixture.id] = {
                    value: null,
                    fillPctg: fillPctgPerPlayer[playerId][fixture.id]
                };
            } else {
                player.stats[fixture.id].fillPctg = fillPctgPerPlayer[playerId][fixture.id];
            }
        });
    });

    const rowValues: RowValues[] = Object.entries(playerStatsMap).map(([playerId, data]) => ({
        id: playerId,
        name: data.name,
        valuesByFixtureId: data.stats,
        imagePath: data.imagePath,
        link: `/player/${playerId}`,
        team_id: data.team_id
    })).sort((a, b) => a.name.localeCompare(b.name));

    if (activePlayers && typeFilter) {
        const lastGames = [...fixtureInfos].sort((a, b) => b.starting_at_timestamp - a.starting_at_timestamp).slice(0, 5);
        const filterRowValues = rowValues.filter((player) => {
            if (typeFilter == 'team') {
                const hasStatsInLastGames = lastGames.some((game) => {
                    return !!minsPlayedPerPlayer[parseInt(player.id)]?.[game.id];
                });
                return hasStatsInLastGames;
            } else if (typeFilter == 'league') {
                const playerTeamId = player.team_id;
                const lastRelevantGames = fixtureInfos.filter(
                    (game) => game.home_id === playerTeamId || game.away_id === playerTeamId
                ).slice(0, 5)
                if (lastRelevantGames.length >= 5) {
                    const playedAny = lastRelevantGames.some((game) => {
                        return !!minsPlayedPerPlayer[parseInt(player.id)]?.[game.id];
                    });
                    return playedAny;
                } else if (lastRelevantGames.length > 0) {
                    const playedAll = lastRelevantGames.every((game) => {
                        return !!minsPlayedPerPlayer[parseInt(player.id)]?.[game.id];
                    });
                    return playedAll;
                }
                return false;
            }
        });
        return filterRowValues;
    }

    return rowValues;
}

export function fillNullDataForPlayerStats(data: any) {
    var coverage: string[] = [];

    data.forEach((stat: any) => {
        // stat is an object, each key is the stats name and the value is the value of the stat
        Object.keys(stat.data).forEach((key: string) => {
            if (!coverage.includes(key)) {
                coverage.push(key);
            }
        });
    });

    PLAYER_STAT_KEYS.forEach((stat: string) => {
        if (!coverage.includes(stat)) {
            coverage.push(stat);
        }
    });

    data.forEach((stat: any) => {
        coverage.forEach((key: string) => {
            if (!stat.data[key] && stat.data[key] !== 0) {
                if (stat.data['MINUTES_PLAYED'])
                    stat.data[key] = 0;
            }
        });
    });
    return data;
}

export function fillNullDataForFixtureStats(data: any) {
    // if the stat is present for home team but not for away team, set it to 0
    // for away team and vice versa
    var coverage: string[] = [];
    data.forEach((stat: any) => {
        Object.keys(stat.data.home).forEach((key: string) => {
            if (!stat.data.away[key] && stat.data.away[key] !== 0) {
                stat.data.away[key] = 0;
            }
            if (stat.data.home[key])
                stat.data.home[key] = Math.round(stat.data.home[key] * 10) / 10;
            if (!coverage.includes(key))
                coverage.push(key);
        });

        Object.keys(stat.data.away).forEach((key: string) => {
            if (!stat.data.home[key] && stat.data.home[key] !== 0) {
                stat.data.home[key] = 0;
            }
            if (stat.data.away[key])
                stat.data.away[key] = Math.round(stat.data.away[key] * 10) / 10;
            if (!coverage.includes(key))
                coverage.push(key);
        });
    });
    return data;
}

function normalizeValue(value: number, fillPctg: number | null | undefined) {
    if (!fillPctg) return value;
    if (value === null || value === undefined) return 0;
    return value / (fillPctg / 100);
}

export function getDeltaFillPctg(fillPctg: number | number[] | undefined | null) {
    if (!fillPctg) return;
    if (Array.isArray(fillPctg)) {
        return (fillPctg[1] || 90) - (fillPctg[0] || 0);
    }
    return fillPctg;
}

// Calculated columns
export const TotalCalculatedColumn = {
    name: 'stats.stats_2',
    tooltipText: 'stats.stats_1',
    calcFn: (values: CellValue[]) => {
        return values.reduce((acc, val) => {
            if (val.value !== null && val.value !== undefined)
                return acc + normalizeValue(val.value, getDeltaFillPctg(val.fillPctg));
            return acc;
        }, 0)
    }
}

export const StreakPctgCalculatedColumn = {
    name: 'stats.stats_4',
    tooltipText: 'stats.stats_3',
    calcFn: (values: CellValue[], minThreshold: number) => {
        let len = 0;
        let total = values.reduce((acc, val) => {
            if (val.value !== null && val.value !== undefined) {
                len += 1;
                if (val.value > minThreshold)
                    return acc + 1;
            }
            return acc;
        }, 0);
        if (!len) return null;
        return total / len * 100;
    },
    calcCounterFn: (values: CellValue[], minThreshold: number) => {
        let len = 0;
        let total = values.reduce((acc, val) => {
            if (val.lowRightText !== null && val.lowRightText !== undefined) {
                len += 1;
                if (Number(val.lowRightText.props.children) > minThreshold)
                    return acc + 1;
            }
            return acc;
        }, 0);
        if (!len) return null;
        return total / len * 100;
    }
}

export const AverageCalculatedColumn = {
    name: 'stats.stats_6',
    tooltipText: 'stats.stats_5',
    calcFn: (values: CellValue[]) => {
        let len = 0;
        let total = values.reduce((acc, val) => {
            if (val.value !== null && val.value !== undefined) {
                len += 1;
                return acc + normalizeValue(val.value, getDeltaFillPctg(val.fillPctg));
            }
            return acc;
        }, 0)
        if (!len) return null;
        return total / len;
    },
    calcCounterFn: (values: CellValue[]) => {
        let len = 0;
        let total = values.reduce((acc, val) => {
            if (val.lowRightText !== null && val.lowRightText !== undefined) {
                len += 1;
                return acc + normalizeValue(Number(val.lowRightText.props.children), getDeltaFillPctg(val.fillPctg));
            }
            return acc;
        }, 0)
        if (!len) return null;
        return total / len;
    },
}

export const AverageCalculatedColumnNOTP90 = {
    name: 'stats.stats_7',
    calcFn: (values: CellValue[]) => {
        let len = 0;
        let total = values.reduce((acc, val) => {
            if (val.value !== null && val.value !== undefined) {
                len += 1;
                return acc + val.value;
            }
            return acc;
        }, 0)
        if (!len) return null;
        return total / len;
    },
    calcCounterFn: (values: CellValue[]) => {
        let len = 0;
        let total = values.reduce((acc, val) => {
            if (val.lowRightText !== null && val.lowRightText !== undefined) {
                len += 1;
                return acc + Number(val.lowRightText.props.children);
            }
            return acc;
        }, 0)
        if (!len) return null;
        return total / len;
    },
}

export const filterFixtures = (data: PlayerStats[] | FixtureStats[], filter: string | null, games?: FixtureInfo[]) => {
    if (!games) {
        return data.map((item: any) => {
            if (filter === 'home' || filter === 'away') {
                const oppositeKey = filter === 'home' ? 'away' : 'home';
                return {
                    ...item,
                    data: {
                        ...item.data,
                        [oppositeKey]: {}
                    },
                };
            }
            return item;
        });
    } else {
        return data.map((player: any) => {
            const game = games.find((game: any) => game.id === player.fixture_id);
            if (!game) return player;
            const isHome = player.team_id === game.home_id;
            if ((isHome && filter === 'away') || (!isHome && filter === 'home')) {
                return {
                    ...player,
                    data: {}
                };
            }
            return player;
        });
    }
}